/**
 * pricing.js — Logique commerciale FG-OS
 *
 * Toute la regle metier de tarification, seuils CGV et remuneration
 * variable est centralisee ici. Fonctions pures, zero side-effect.
 *
 * Sources :
 *   - CGV Espace Conso 2026 (minimum facturation, franco, port)
 *   - Plan de remuneration variable Wissam (paliers + ouvertures compte)
 *
 * Utilise par :
 *   - Module Chiffrage (calcul devis + comparateur Amazon)
 *   - Module Dashboard (compteur CA + paliers + primes temps reel)
 */

// ============================================================
// Constantes metier — CGV Espace Conso 2026
// ============================================================

/**
 * Seuils commerciaux figes dans les CGV Espace Conso 2026.
 * Toute modification = avenant CGV requis (jamais hardcoder ailleurs).
 */
export const CGV = Object.freeze({
  /** Minimum de facturation par commande (€ HT). En dessous : commande refusee. */
  MIN_FACTURATION_HT: 250,
  /** Franco de port atteint a partir de ce montant (€ HT). */
  FRANCO_HT: 300,
  /** Frais de port factures si commande < FRANCO_HT (€ HT). */
  PORT_HT: 12,
  /** Montant minimum d'une commande pour qu'une ouverture de compte declenche la prime (€ HT). */
  COMPTE_PRIME_MIN_HT: 1500,
});

// ============================================================
// Constantes metier — Remuneration variable Wissam
// ============================================================

/**
 * Paliers de CA mensuel global (toutes ventes confondues, FG inclus).
 * Tries ordre croissant, valeurs en euros HT.
 */
export const PALIERS_CA = Object.freeze([20000, 33000, 60000, 80000]);

/** Prime versee par palier franchi, cumulative (€). */
export const PRIME_PAR_PALIER = 300;

/** Prime versee par ouverture de compte eligible (€). */
export const PRIME_OUVERTURE_COMPTE = 50;

// ============================================================
// Calcul commande / devis
// ============================================================

/**
 * Calcule le sous-total HT d'une liste de lignes.
 *
 * @param {Array<{ pa_ht: number, quantite: number }>} lignes
 * @returns {number} sous-total HT (rounded a 2 decimales)
 */
export function calcSousTotal(lignes) {
  if (!Array.isArray(lignes) || lignes.length === 0) return 0;
  const total = lignes.reduce((sum, l) => {
    const prix = Number(l?.pa_ht) || 0;
    const qte = Number(l?.quantite) || 0;
    return sum + prix * qte;
  }, 0);
  return roundEUR(total);
}

/**
 * Determine les frais de port a appliquer.
 * Regle CGV : 0 € si sous-total >= FRANCO_HT, sinon PORT_HT.
 *
 * @param {number} sousTotal en € HT
 * @returns {number} port en € HT (0 ou 12)
 */
export function calcPort(sousTotal) {
  return sousTotal >= CGV.FRANCO_HT ? 0 : CGV.PORT_HT;
}

/**
 * Calcul complet d'une commande.
 * Retourne tous les indicateurs necessaires a l'UI Chiffrage.
 *
 * @param {Array} lignes
 * @returns {{
 *   sousTotal: number,
 *   port: number,
 *   total: number,
 *   atteintMin: boolean,
 *   atteintFranco: boolean,
 *   manquePourMin: number,
 *   manquePourFranco: number,
 * }}
 */
export function calcTotal(lignes) {
  const sousTotal = calcSousTotal(lignes);
  const port = calcPort(sousTotal);
  const total = roundEUR(sousTotal + port);

  const atteintMin = sousTotal >= CGV.MIN_FACTURATION_HT;
  const atteintFranco = sousTotal >= CGV.FRANCO_HT;
  const manquePourMin = atteintMin ? 0 : roundEUR(CGV.MIN_FACTURATION_HT - sousTotal);
  const manquePourFranco = atteintFranco ? 0 : roundEUR(CGV.FRANCO_HT - sousTotal);

  return {
    sousTotal,
    port,
    total,
    atteintMin,
    atteintFranco,
    manquePourMin,
    manquePourFranco,
  };
}

// ============================================================
// Remuneration variable — paliers de CA mensuel
// ============================================================

/**
 * Calcule l'etat des paliers franchis pour un CA mensuel global donne.
 * Prime CUMULATIVE : 1 palier = 300 €, 2 paliers = 600 €, etc.
 *
 * @param {number} caGlobal CA mensuel total HT (toutes ventes, FG inclus)
 * @returns {{
 *   nbFranchis: number,
 *   primesPaliers: number,
 *   prochainPalier: number | null,
 *   manquePourProchain: number,
 *   palierActuel: number | null,
 * }}
 */
export function calcPaliersFranchis(caGlobal) {
  const ca = Math.max(0, Number(caGlobal) || 0);

  let nbFranchis = 0;
  let palierActuel = null;
  for (const palier of PALIERS_CA) {
    if (ca >= palier) {
      nbFranchis++;
      palierActuel = palier;
    } else {
      break;
    }
  }

  const prochainPalier = nbFranchis < PALIERS_CA.length ? PALIERS_CA[nbFranchis] : null;
  const manquePourProchain = prochainPalier !== null ? roundEUR(prochainPalier - ca) : 0;

  return {
    nbFranchis,
    primesPaliers: nbFranchis * PRIME_PAR_PALIER,
    prochainPalier,
    manquePourProchain,
    palierActuel,
  };
}

// ============================================================
// Remuneration variable — ouvertures de compte
// ============================================================

/**
 * Calcule la prime totale des ouvertures de compte du mois.
 * Une ouverture est eligible si le montant de la commande >= COMPTE_PRIME_MIN_HT (1500 €).
 *
 * @param {Array<{ montant: number }>} ouvertures
 * @returns {{
 *   eligibles: number,
 *   inEligibles: number,
 *   primeTotale: number,
 * }}
 */
export function calcPrimesOuvertures(ouvertures) {
  if (!Array.isArray(ouvertures) || ouvertures.length === 0) {
    return { eligibles: 0, inEligibles: 0, primeTotale: 0 };
  }
  let eligibles = 0;
  let inEligibles = 0;
  for (const o of ouvertures) {
    const m = Number(o?.montant) || 0;
    if (m >= CGV.COMPTE_PRIME_MIN_HT) eligibles++;
    else inEligibles++;
  }
  return {
    eligibles,
    inEligibles,
    primeTotale: eligibles * PRIME_OUVERTURE_COMPTE,
  };
}

/**
 * Remuneration variable totale du mois.
 * Somme : primes paliers cumulatifs + primes ouvertures compte eligibles.
 *
 * @param {number} caGlobal
 * @param {Array<{ montant: number }>} ouvertures
 * @returns {{
 *   paliers: ReturnType<typeof calcPaliersFranchis>,
 *   ouvertures: ReturnType<typeof calcPrimesOuvertures>,
 *   total: number,
 * }}
 */
export function calcRemunerationTotale(caGlobal, ouvertures) {
  const paliers = calcPaliersFranchis(caGlobal);
  const ouv = calcPrimesOuvertures(ouvertures);
  return {
    paliers,
    ouvertures: ouv,
    total: paliers.primesPaliers + ouv.primeTotale,
  };
}

/**
 * Projette le CA mensuel a fin de mois sur la base de la cadence journaliere
 * observee depuis le debut du mois.
 *
 * Utile pour le Dashboard : Wissam veut savoir si, au rythme actuel, il va
 * franchir le prochain palier de prime avant la fin du mois (et combien il
 * manque le cas echeant pour ajuster son effort commercial).
 *
 * @param {number} caActuel   CA mensuel HT realise a date (€)
 * @param {number} jourActuel Numero du jour dans le mois (1 = premier jour)
 * @param {number} [joursTotal=30] Nombre de jours du mois (28, 30, 31)
 * @returns {{
 *   cadenceJournaliere: number,
 *   caProjeteFin: number,
 *   joursRestants: number,
 *   prochainPalier: number | null,
 *   atteindraProchain: boolean,
 *   manqueProjecte: number,
 * }}
 *
 * Edge cases :
 *   - jourActuel < 1 : clamp a 1 pour eviter division par zero
 *   - caActuel = 0   : cadence 0, projete 0, n'atteindra pas le prochain
 *   - prochainPalier = null (deja 80k+) : atteindraProchain = false, manque 0
 *   - joursTotal < jourActuel : clamp a jourActuel (sinon negatif)
 */
export function calcProjectionCA(caActuel, jourActuel, joursTotal = 30) {
  const ca = Math.max(0, Number(caActuel) || 0);
  const jour = Math.max(1, Number(jourActuel) || 1);
  const total = Math.max(jour, Number(joursTotal) || 30);

  const cadenceJournaliere = roundEUR(ca / jour);
  const caProjeteFin = roundEUR(cadenceJournaliere * total);
  const joursRestants = total - jour;

  const { prochainPalier } = calcPaliersFranchis(ca);
  const atteindraProchain = prochainPalier !== null && caProjeteFin >= prochainPalier;
  const manqueProjecte = prochainPalier !== null
    ? Math.max(0, roundEUR(prochainPalier - caProjeteFin))
    : 0;

  return {
    cadenceJournaliere,
    caProjeteFin,
    joursRestants,
    prochainPalier,
    atteindraProchain,
    manqueProjecte,
  };
}

// ============================================================
// Comparateur Amazon Business
// ============================================================

/**
 * Calcule l'economie client par rapport au prix Amazon Business.
 * Si prixAmazon <= prixNotre : economie negative clampe a 0 + flag perte.
 *
 * @param {number} prixNotre  prix Espace Conso HT
 * @param {number} prixAmazon prix Amazon Business HT (saisi par Wissam ou estime)
 * @returns {{
 *   economie: number,
 *   pourcentage: number,
 *   perteVsAmazon: boolean,
 * }}
 */
export function calcEconomieClient(prixNotre, prixAmazon) {
  const n = Number(prixNotre) || 0;
  const a = Number(prixAmazon) || 0;

  if (a <= 0) {
    return { economie: 0, pourcentage: 0, perteVsAmazon: false };
  }
  const diff = a - n;
  if (diff <= 0) {
    return { economie: 0, pourcentage: 0, perteVsAmazon: true };
  }
  return {
    economie: roundEUR(diff),
    pourcentage: roundPct((diff / a) * 100),
    perteVsAmazon: false,
  };
}

// ============================================================
// Helpers formatage
// ============================================================

/**
 * Arrondi a 2 decimales (centime d'euro), evite les artefacts flottants.
 */
export function roundEUR(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Arrondi a 1 decimale pour pourcentages.
 */
export function roundPct(n) {
  return Math.round((Number(n) || 0) * 10) / 10;
}

/**
 * Formate un montant en EUR HT pour affichage UI.
 * Exemple : 1234.5 -> "1 234,50 € HT"
 *
 * SSR-safe : si Intl indisponible, fallback simple.
 */
export function formatEUR(n, { withHT = true } = {}) {
  const v = Number(n) || 0;
  let formatted;
  try {
    formatted = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);
  } catch {
    formatted = `${v.toFixed(2).replace('.', ',')} €`;
  }
  return withHT ? `${formatted} HT` : formatted;
}

/**
 * Formate un pourcentage. Exemple : 23.5 -> "23,5 %"
 */
export function formatPct(n) {
  const v = Number(n) || 0;
  return `${v.toFixed(1).replace('.', ',')} %`;
}
